import { computed, Directive, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BasketService } from '@basket';
import { CatalogueService } from '@catalogue';
import {
  DocumentPathService,
  JourneyStepTrackerService,
  JourneyType,
} from '@common';
import { BenefitsListItem } from '@components';
import { CONFIG } from '@config';
import { CustomerService } from '@customer';
import { CoverDetailsService, GADataService } from '@data-access';
import { extractPageNameFromUrl, GAJourneyVersion, GATag } from '@ga';
import { TranslateService } from '@i18n';
import { PosthogTrackingEvents, TrackingService } from '@tracking';
import { isAfter, subMonths } from 'date-fns';
import posthog from 'posthog-js';
import { FeatureSinglePlanTranslationDictionary } from '../../i18n/feature-single-plan-dictionary.interface';
import { SinglePlanService } from '../../services/single-plan/single-plan.service';

@Directive()
export abstract class SinglePlanSaleBaseComponent implements OnInit {
  protected abstract readonly featureFlagVariant: 'control' | 'test';

  protected readonly basketService = inject(BasketService);
  protected readonly config = inject(CONFIG);
  protected readonly customerService = inject(CustomerService);
  protected readonly documentPathService = inject(DocumentPathService);
  protected readonly catalogueService = inject(CatalogueService);
  protected readonly gaDataService = inject(GADataService);
  protected readonly journeyStepTracker = inject(JourneyStepTrackerService);
  protected readonly router = inject(Router);
  protected readonly singlePlanService = inject(SinglePlanService);
  protected readonly coverDetailsService = inject(CoverDetailsService);
  protected readonly trackingService = inject(TrackingService);
  protected readonly translateService = inject(
    TranslateService<FeatureSinglePlanTranslationDictionary>,
  );

  private readonly vm = this.basketService.vm();

  readonly selectedAppliance = this.basketService.basketItemApplianceDetails();
  readonly selectedQuotePaymentOption =
    this.basketService.basketItemQuoteDetailsPaymentOption();
  readonly selectedApplianceContent =
    this.singlePlanService.selectedApplianceContent();

  readonly exclusions = this.coverDetailsService.getExclusions;
  readonly clientName = this.config.clientName;
  readonly hasDiscountedMonths = () =>
    this.basketService.basketItemQuoteHasDiscountedMonths();
  readonly hasFreeMonths = () =>
    this.basketService.basketItemQuoteHasFreeMonths();
  readonly hasPromo = () => this.basketService.basketItemQuoteHasPromo();
  readonly isOneOffPayment = () =>
    this.basketService.basketItemQuoteIsOneOffPayment();

  readonly warrantyInMonths = () =>
    this.catalogueService.steppedFormApplianceWarranty();

  readonly sharedPartialGATag: GATag = {
    multiPlanOffer: this.gaDataService.multiPlanOffer(),
    offerStatus: this.gaDataService.offerStatus(),
    offerPrice: this.vm.basketItemQuoteDetailsPaymentOption?.subsequentPayment,
    ...this.gaDataService.preQuoteAppliance(),
  };

  readonly benefitsListItemsAdditional: BenefitsListItem[] = [
    {
      iconName: 'screwdriver-wrench',
      text: this.translateService.translate(
        'COVER_DETAILS_ADDITIONAL_EXPERT_REPAIRS',
      ),
    },
    {
      iconName: 'arrow-pointer',
      text: this.translateService.translate(
        'COVER_DETAILS_ADDITIONAL_ONLINE_BOOKING',
      ),
    },
  ];

  readonly benefitsListItemsNotCovered: BenefitsListItem[] =
    this.coverDetailsService.getExclusions().map((text) => ({
      iconName: 'circle-xmark',
      text,
    }));

  readonly applianceInGuarantee = computed(() =>
    isAfter(
      this.catalogueService.selectedAppliancePurchaseDate(),
      subMonths(new Date(), 3),
    ),
  );

  ngOnInit(): void {
    this.customerService.setCustomerSelectedToRegisterOnly(false);
    this.documentPathService.setJourneyType(JourneyType.ApplianceCare);
    this.journeyStepTracker.updateStep(
      2,
      3,
      this.translateService.translate('STEP_TITLE_EXTRA_COVER_OPTIONAL'),
      false,
    );
    posthog.capture(PosthogTrackingEvents.SinglePlanProtectionPageLoaded, {
      featureFlag: 'AI-TEST',
      featureFlagVariant: this.featureFlagVariant,
    });
  }

  registerOnlyClicked(): void {
    this.customerService.setCustomerSelectedToPurchasePlan(false);
    this.customerService.setCustomerSelectedToRegisterOnly(true);
    this.gaDataService.setJourneyVersion(GAJourneyVersion.RegisterOnly);

    this.router.navigate(['contact-details']);

    this.trackingService.trackEvent({
      ...this.sharedPartialGATag,
      eventAction: 'enhance',
      eventCategory: extractPageNameFromUrl(),
      eventLabel: 'register',
      journeyType: this.gaDataService.journeyVersion(),
    });
  }

  purchasePlanClicked(): void {
    this.customerService.setCustomerSelectedToPurchasePlan(true);
    this.customerService.setCustomerSelectedToRegisterOnly(false);
    this.gaDataService.setJourneyVersion(GAJourneyVersion.SinglePlan);

    this.trackingService.trackEvent(this.gaDataService.eventAddToCart());
    this.trackingService.trackEvent(this.gaDataService.eventBasketTotal());

    this.router.navigate(['contact-details']);

    this.trackingService.trackEvent({
      ...this.sharedPartialGATag,
      eventAction: 'enhance',
      eventCategory: extractPageNameFromUrl(),
      eventLabel: 'enhance',
      journeyType: this.gaDataService.journeyVersion(),
    });
  }
}
